import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Button, Box, Paper } from '@material-ui/core';
import topImage from '../../img/top.JPG';
import {
  getLatestJobList,
  getLatestFeedList,
  getRegisteredCompanyList,
  getLatestPostList
} from "../../actions/homeActions";

import LatestJobList from "./LatestJobList";
import RegisteredCompaniesInfo from "./RegisteredCompaniesInfo";
import LatestPostList from "./LatestPostList";
import { getNewNotificationNum } from "../../utils/getNotificationNum";

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  section: {
    margin: '5rem 0',
    '& .heading': {
      borderBottom: `double 2px ${theme.palette.primary.main}`,
      marginBottom: '5rem',
      fontSize: '3rem',
      textAlign: 'center',
      color: '#066298',
      fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif",
      [theme.breakpoints.down('sm')]: {
        fontSize: '2.4rem',
      },
    },
  },
  header: {
    backgroundImage: `url(${topImage})`,
    backgroundSize: 'cover',
    height: '50rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      height: '30rem',
    },
  },
  headerContainer: {
    backgroundColor: 'rgba(10, 10, 10, 0.5)',
    color: theme.palette.common.white,
    padding: '5rem',
    borderRadius: '10px',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%',
      padding: '1rem 2rem',
      borderRadius: '0',
      fontSize: '1.4rem',
    },
  },
  headerContent: {
    textAlign: 'left',
    maxWidth: '70rem',
    marginTop: '2rem',
    fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif",
    '& span': {
      display: 'inline-block',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '0.5rem',
    },
  },
  headerTitle: {
    fontSize: '3.5rem',
    fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif !important",
    '& span': {
      display: 'inline-block',
      fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif !important",
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.4rem',
    },
  },
  signupButton: {
    fontSize: '1.6rem',
    padding: '0.75rem 1.5rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  content: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    padding: '2rem',
  },
  mainContent: {
    flex: 1,
  },
  sidebar: {
    maxWidth: '38rem',
    minWidth: '35rem',
    padding: '1.3rem',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  sidebarHeading: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: '0.5rem 1rem',
    marginBottom: '1rem',
  },
}));

function Home() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const latestJobList = useSelector(state => state.home.latestJobList);
  const latestPostList = useSelector(state => state.home.latestPostList);
  const notifications = useSelector(state => state.user.notifications);
  let notificationNum = getNewNotificationNum(notifications);

  useEffect(() => {
    dispatch(getLatestJobList());
    dispatch(getLatestPostList());
    dispatch(getLatestFeedList());
    dispatch(getRegisteredCompanyList());
  }, [dispatch]);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>
          {notificationNum ? `(${notificationNum})` : ""}ＮＺワークス |
          ニュージーランドの求人検索サイト
        </title>
        <meta
          name="description"
          content="ニュージーランドの日本人向け求人情報を投稿・検索できます。スタッフ募集中の経営者の方、仕事を探している永住者、ワーキングホリデーメーカーいずれの方も是非ご利用ください！"
        />
      </Helmet>

      <Box className={classes.header}>
        <Paper elevation={0} className={classes.headerContainer}>
          <Typography
            variant="h1"
            className={classes.headerTitle}
            sx={{
              fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif",
              '& span': {
                fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif"
              }
            }}
          >
            <span>ニュージーランドの</span>
            <span>求人情報サイト</span>
          </Typography>
          <Typography className={classes.headerContent}>
            ニュージーワークスは、ニュージーランド在住の日本人向け求人情報サイトです。
            <span>日本人スタッフを募集したい経営者の方、</span>
            <span>ニュージーランドで仕事を探している方、</span>
            <span>どなたでもお気軽にご利用ください！</span>
          </Typography>
          <Box mt={3}>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              color="primary"
              className={classes.signupButton}
            >
              アカウント登録
            </Button>
          </Box>
        </Paper>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.mainContent}>
          <Box className={classes.section}>
            <Typography variant="h2" className="heading">
              最近の求人
            </Typography>
            <Box mt={2}>
              <Container padding={2}>
                <LatestJobList jobList={latestJobList} />
              </Container>
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h2" className="heading">
              最近の投稿
            </Typography>
            <LatestPostList postList={latestPostList} />
          </Box>
        </Box>

        <Box className={classes.sidebar}>
          <Box mb={4}>
            <Typography variant="h3" className="section-title">
              最近更新があったアカウント
            </Typography>
            <RegisteredCompaniesInfo />
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default Home;
